import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { classNames, redirectUrl } from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import React, { useMemo, useState } from "react";
import { CaretRight } from "@phosphor-icons/react";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getRequest } from "lib/core/apiClient";
import {
  getUnApplicablePaymentReason,
  handlePaymentError,
  parseMethodUiAttributes,
} from "lib/utils/payments";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLocale } from "lib/hooks/useLocale";
import BajajIcon from "assests/images/bajaj.png";
import SuggestedMethodWrapper from "components/common/SuggestedMethodWrapper";
import LogosStack from "components/common/LogosStack";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { PaymentMode } from "lib/types/payments";
import { getItems } from "lib/utils/checkout";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

const CustomPaymentCard: React.FC = ({}) => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();

  const {
    state: { checkoutId, hasDefaultShippingHandleSelected },
    actions: { setOOSItems },
  } = useCheckoutContext();
  const {
    state: { paymentMethods },
  } = usePaymentContext();

  const {
    state: { checkoutMetadata },
  } = useMerchantContext();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const customModeConfig = paymentMethods?.find((e: PaymentMode) => e.name === "CUSTOM_PAYMENT");
  const unapplicabilityReason = getUnApplicablePaymentReason(
    customModeConfig?.unavailabilityReasons || [],
    customModeConfig?.metadata?.title || "",
  );

  const methodUiAttributes = useMemo(() => {
    return parseMethodUiAttributes({ mode: customModeConfig, checkoutMetadata });
  }, [customModeConfig, checkoutMetadata]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCustomPaymentClick = async () => {
    if (!hasDefaultShippingHandleSelected) {
      openPopup("SHIPPING_HANDLES", {});
      return;
    }

    try {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
        eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
        eventType: "click",
        metaData: {
          methodType: "CUSTOM_PAYMENT",
        },
      });
      setIsLoading(true);
      const response = await getRequest(`/checkout/${checkoutId}/custom-payment/initiate`, "KRATOS_PRIVATE");
      const success_url = response?.metadata?.action_urls?.CUSTOM_PAYMENT_TILE?.success_url;
      if (success_url) {
        redirectUrl(success_url);
      } else {
        throw new Error("Error while applying gift cards");
      }
    } catch (e: any) {
      handlePaymentError({
        error: e,
        callbacks: {
          showErrorToast: errorToast,
          getTranslation: t,
          setOOSItems: (items) => setOOSItems(getItems(items, true) ?? []),
          openPopup,
          onOutOfStock: (items) =>
            sendAnalyticsEvent({
              eventName: analyticsEvents.FLO_ITEM_OOS,
              eventFor: [analyticsTypes.SF_ANALYTICS],
              eventType: "click",
              metaData: {
                outOfStockItems: items,
              },
            }),
        },
        payload: {
          checkoutId,
          payment_mode: "CUSTOM_PAYMENT",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderCustomPaymentMethod = () => {
    return (
      <div
        className={classNames(
          `group flex items-center px-3.5 py-2.5 border rounded-2xl gap-3 cursor-pointer w-full min-h-14 `,
          customModeConfig?.isAvailable ? "hover:shadow-[0_0_0_100px_rgba(113,113,123,0.15)_inset]" : "",
        )}
        style={{
          borderColor: methodUiAttributes.paymentCard.borderColor,
          backgroundColor: methodUiAttributes.paymentCard.backgroundColor,
        }}>
        {methodUiAttributes.layout === "ICONS" && (
          <div className="flex h-9 w-9 items-center justify-center">
            <img
              src={BajajIcon}
              alt="Bajaj"
              width={36}
              height={36}
              style={{
                opacity: customModeConfig?.isAvailable ? 1 : 0.5,
              }}
            />
          </div>
        )}

        <div className="flex w-full flex-1 flex-col items-start justify-start space-y-1">
          <div className="flex gap-1 items-center">
            <h3
              className="text-sm font-medium"
              style={{
                color: customModeConfig?.isAvailable
                  ? methodUiAttributes.paymentCard.textColor
                  : methodUiAttributes.paymentCard.subtitleColor,
              }}>
              {methodUiAttributes.title ? methodUiAttributes.title : customModeConfig?.metadata?.title ?? ""}
            </h3>
            {methodUiAttributes.layout === "LOGOS" && <LogosStack logos={methodUiAttributes.icons} />}
          </div>
          {!methodUiAttributes.paymentCard.isSubtitleHidden && (
            <p
              className="text-left text-xs font-normal"
              style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {methodUiAttributes.subtitle
                ? methodUiAttributes.subtitle
                : customModeConfig?.metadata?.description ?? ""}
            </p>
          )}
          {!customModeConfig?.isAvailable && unapplicabilityReason && (
            <div className="bg-gray-lightest">
              <p className="px-3 py-2 text-left text-xs font-normal text-gray-dark">
                {unapplicabilityReason}
              </p>
            </div>
          )}
        </div>

        <CaretRight className="h-4 w-4" style={{ color: methodUiAttributes.paymentCard.textColor }} />
      </div>
    );
  };

  return (
    <>
      <SuggestedMethodWrapper
        isSuggested={methodUiAttributes.isSuggested}
        title={methodUiAttributes.suggestedTile?.title}
        backgroundColor={methodUiAttributes.suggestedTile?.backgroundColor}
        textColor={methodUiAttributes.suggestedTile?.textColor}>
        <button
          className={classNames(
            "w-full",
            customModeConfig?.isAvailable ? "cursor-pointer" : "cursor-not-allowed",
          )}
          disabled={!customModeConfig?.isAvailable}
          onClick={handleCustomPaymentClick}>
          {renderCustomPaymentMethod()}
        </button>
      </SuggestedMethodWrapper>
      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default CustomPaymentCard;
